import { DataModel } from "../../base/index.js";
import type {
  DnsRecordCombinedACustomData,
  DnsRecordCombinedAData,
  DnsRecordCombinedAManagedData,
  DnsRecordCombinedAUnsetData,
} from "./types.js";
import { Ingress } from "../Ingress/index.js";
import {
  DnsRecordSettings,
  type DnsRecordSettingsData,
} from "../DnsRecordSettings/index.js";
import type { DnsZoneCommon } from "../DnsZone/index.js";

abstract class DnsRecordCombinedABase<
  T extends DnsRecordCombinedAData,
> extends DataModel<T> {
  public readonly dnsZone: DnsZoneCommon;
  public constructor(dnsZone: DnsZoneCommon, data: T) {
    super(data);
    this.dnsZone = dnsZone;
  }

  public async addEntry(
    type: "a" | "aaaa",
    entry: string,
    settings: DnsRecordSettingsData,
  ) {
    const record = this.dnsZone.recordSet.combinedA;
    const existingARecords = record.type === "custom" ? record.a : [];
    const existingAaaaRecords = record.type === "custom" ? record.aaaa : [];

    if (type === "a") {
      return await this.dnsZone.setCustomARecord(
        [...existingARecords, entry],
        existingAaaaRecords,
        settings,
      );
    }
    return await this.dnsZone.setCustomARecord(
      existingARecords,
      [...existingAaaaRecords, entry],
      settings,
    );
  }

  public async removeEntry(type: "a" | "aaaa", entry: string) {
    const record = this.dnsZone.recordSet.combinedA;
    if (record.type !== "custom") {
      return;
    }
    if (type === "aaaa") {
      return await this.dnsZone.setCustomARecord(
        record.a,
        record.aaaa.filter((i) => i === entry),
        record.settings.data,
      );
    }
    return await this.dnsZone.setCustomARecord(
      record.a.filter((i) => i === entry),
      record.aaaa,
      record.settings.data,
    );
  }

  public async updateEntry(
    type: "a" | "aaaa",
    entry: string,
    newEntry: string,
    settings: DnsRecordSettingsData,
  ) {
    const record = this.dnsZone.recordSet.combinedA;
    if (record.type !== "custom") {
      return;
    }
    if (type === "aaaa") {
      return await this.dnsZone.setCustomARecord(
        record.a,
        record.aaaa.map((i) => (i === entry ? newEntry : i)),
        settings,
      );
    }
    return await this.dnsZone.setCustomARecord(
      record.a.map((i) => (i === entry ? newEntry : i)),
      record.aaaa,
      record.settings.data,
    );
  }

  public async setManaged() {
    await this.dnsZone.setRecordManaged("a");
  }
}

export class DnsRecordCombinedAManaged extends DnsRecordCombinedABase<DnsRecordCombinedAManagedData> {
  public readonly type = "managed";
  public readonly managedBy: Ingress;
  public constructor(
    dnsZone: DnsZoneCommon,
    data: DnsRecordCombinedAManagedData,
  ) {
    super(dnsZone, data);
    this.managedBy = Ingress.ofId(data.managedBy.ingressId);
  }

  public getTtl(): number | "auto" {
    return "auto";
  }
}

export class DnsRecordCombinedACustom extends DnsRecordCombinedABase<DnsRecordCombinedACustomData> {
  public readonly type = "custom";

  public readonly a: string[];
  public readonly aaaa: string[];
  public readonly settings: DnsRecordSettings;

  public constructor(
    dnsZone: DnsZoneCommon,
    data: DnsRecordCombinedACustomData,
  ) {
    super(dnsZone, data);
    this.a = data.a;
    this.aaaa = data.aaaa;
    this.settings = new DnsRecordSettings(data.settings);
  }

  public getTtl(): number | "auto" {
    return this.settings.ttl;
  }
}

export class DnsRecordCombinedAUnset extends DnsRecordCombinedABase<DnsRecordCombinedAUnsetData> {
  public readonly type = "unset";
  public constructor(
    dnsZone: DnsZoneCommon,
    data: DnsRecordCombinedAUnsetData,
  ) {
    super(dnsZone, data);
  }

  public getTtl(): number | "auto" {
    return "auto";
  }
}

export type DnsRecordCombinedA =
  | DnsRecordCombinedAManaged
  | DnsRecordCombinedACustom
  | DnsRecordCombinedAUnset;

export const dnsRecordCombinedAFactory = (
  dnsZone: DnsZoneCommon,
  data: DnsRecordCombinedAData,
): DnsRecordCombinedA => {
  if ("a" in data) {
    return new DnsRecordCombinedACustom(dnsZone, data);
  }
  if ("managedBy" in data) {
    return new DnsRecordCombinedAManaged(dnsZone, data);
  }
  return new DnsRecordCombinedAUnset(dnsZone, data);
};
